import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FaChartBar, FaUsers, FaVideo, FaClock, FaEye, FaArrowLeft, FaDownload, FaTable, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  color: white;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    padding: 15px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    border-radius: 10px;
  }
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 10px 15px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    align-self: flex-start;
    padding: 8px 12px;
    font-size: 14px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 15px;
  
  svg {
    color: #ffd700;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: center;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    gap: 8px;
    
    svg {
      font-size: 1.5rem;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  color: #ffd700;
  margin-bottom: 10px;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  opacity: 0.8;
`;

const ChartSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ChartTitle = styled.h2`
  margin: 0 0 20px 0;
  font-size: 1.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const TableHeader = styled.th`
  padding: 15px;
  text-align: left;
  border-bottom: 2px solid rgba(255, 255, 255, 0.3);
  font-weight: 600;
  font-size: 1.1rem;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const TableCell = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid rgba(255, 0, 0, 0.5);
  border-radius: 10px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
`;

const LogoutButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

const ExportButton = styled.button`
  background: linear-gradient(45deg, #28a745, #20c997);
  border: none;
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: linear-gradient(45deg, #218838, #1ea085);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(40, 167, 69, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  padding: 30px;
  width: 95%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  gap: 15px;
  
  svg {
    color: #ffd700;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 10px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const SummaryCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
`;

const SummaryValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #ffd700;
  margin-bottom: 5px;
`;

const SummaryLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  color: white;
`;

const AdvancedTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
`;

const AdvancedTableHeader = styled.th`
  padding: 15px 12px;
  text-align: left;
  background: rgba(255, 255, 255, 0.2);
  font-weight: 600;
  font-size: 0.9rem;
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const AdvancedTableRow = styled.tr`
  &:nth-child(even) {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const AdvancedTableCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.85rem;
  color: white;
  
  &.subscriber-id {
    font-family: monospace;
    font-size: 0.8rem;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  &.video-id {
    font-family: monospace;
    font-size: 0.8rem;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  &.timestamp {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const TableContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const formatDuration = (seconds) => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
};

const AnalyticsDashboard = () => {
  const { instance } = useMsal();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAdvancedView, setShowAdvancedView] = useState(false);
  const [advancedData, setAdvancedData] = useState(null);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [advancedExportLoading, setAdvancedExportLoading] = useState(false);
  const [showDebugSection, setShowDebugSection] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const navigate = useNavigate();

  const getApiUrl = () => {
    // If explicitly set in environment, use that (for local development)
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    
    // For production: automatically use the same domain as the frontend
    const currentOrigin = window.location.origin;
    
    // If running on localhost, assume backend is on port 8000
    if (currentOrigin.includes('localhost')) {
      return 'http://localhost:8000';
    }
    
    // For production domains, assume API is on the same domain
    return currentOrigin;
  };

  const getToken = useCallback(async () => {
    const accounts = instance.getAllAccounts();
    const result = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return result.idToken;
  }, [instance]);

  const handleLogout = useCallback(() => {
    instance.logoutRedirect({
      postLogoutRedirectUri: process.env.REACT_APP_ENTRA_REDIRECT_URI,
    });
  }, [instance]);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      const apiUrl = getApiUrl();
      console.log('🌐 Analytics API URL detected:', apiUrl);

      const response = await fetch(`${apiUrl}/api/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setAnalyticsData(result.data);
      } else {
        throw new Error('Failed to fetch analytics data');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, handleLogout]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const handleBack = () => {
    navigate('/');
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const token = await getToken();
      const apiUrl = getApiUrl();
      console.log('📊 Exporting CSV from:', apiUrl);

      const response = await fetch(`${apiUrl}/api/analytics/export/subscriber-views`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the CSV content as blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'subscriber_views_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('📊 CSV export completed successfully');
      
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const fetchAdvancedAnalytics = async () => {
    try {
      setAdvancedLoading(true);
      const token = await getToken();
      const apiUrl = getApiUrl();
      console.log('📊 Fetching advanced analytics from:', apiUrl);

      const response = await fetch(`${apiUrl}/api/analytics/advanced`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setAdvancedData(result.data);
        setShowAdvancedView(true);
      } else {
        throw new Error('Failed to fetch advanced analytics data');
      }
      
    } catch (err) {
      console.error('Error fetching advanced analytics:', err);
      alert('Failed to load advanced analytics. Please try again.');
    } finally {
      setAdvancedLoading(false);
    }
  };

  const handleAdvancedExportCSV = async () => {
    try {
      setAdvancedExportLoading(true);
      const token = await getToken();
      const apiUrl = getApiUrl();
      console.log('📊 Exporting advanced CSV from:', apiUrl);

      const response = await fetch(`${apiUrl}/api/analytics/export/advanced`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the CSV content as blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'advanced_analytics_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('📊 Advanced CSV export completed successfully');
      
    } catch (err) {
      console.error('Error exporting advanced CSV:', err);
      alert('Failed to export advanced CSV. Please try again.');
    } finally {
      setAdvancedExportLoading(false);
    }
  };

  const closeAdvancedView = () => {
    setShowAdvancedView(false);
    setAdvancedData(null);
    setShowDebugSection(false);
    setDebugData(null);
  };

  const fetchDebugData = async () => {
    try {
      setDebugLoading(true);
      const token = await getToken();
      const apiUrl = getApiUrl();
      console.log('🔍 Fetching debug data from:', apiUrl);

      const response = await fetch(`${apiUrl}/api/analytics/debug/files`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setDebugData(result.data);
        setShowDebugSection(true);
      } else {
        throw new Error('Failed to fetch debug data');
      }
      
    } catch (err) {
      console.error('Error fetching debug data:', err);
      alert('Failed to load debug data. Please try again.');
    } finally {
      setDebugLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <Header>
          <BackButton onClick={handleBack}>
            <FaArrowLeft />
            Back
          </BackButton>
          <Title>
            <FaChartBar />
            Analytics Dashboard
          </Title>
          <LogoutButton onClick={handleLogout}>
            Logout
          </LogoutButton>
        </Header>
        <LoadingSpinner>Loading analytics data...</LoadingSpinner>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <Header>
          <BackButton onClick={handleBack}>
            <FaArrowLeft />
            Back
          </BackButton>
          <Title>
            <FaChartBar />
            Analytics Dashboard
          </Title>
          <LogoutButton onClick={handleLogout}>
            Logout
          </LogoutButton>
        </Header>
        <ErrorMessage>
          <h3>Error Loading Analytics</h3>
          <p>{error}</p>
          <button onClick={() => fetchAnalyticsData()} style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '10px'
          }}>
            Retry
          </button>
        </ErrorMessage>
      </DashboardContainer>
    );
  }

  const { video_analytics = [], subscriber_analytics = [], summary = {} } = analyticsData || {};

  // Prepare chart data (top 10 videos)
  const chartData = video_analytics.slice(0, 10).map(video => ({
    video_id: video.video_id.length > 15 ? video.video_id.substring(0, 15) + '...' : video.video_id,
    views: video.total_views,
    completion: video.avg_completion_percentage,
    avg_time: video.avg_watch_time_seconds
  }));

  return (
    <DashboardContainer>
      <Header>
        <BackButton onClick={handleBack}>
          <FaArrowLeft />
          Back
        </BackButton>
        <Title>
          <FaChartBar />
          Analytics Dashboard
        </Title>
        <HeaderActions>
          <ExportButton onClick={handleExportCSV} disabled={exportLoading}>
            <FaDownload />
            {exportLoading ? 'Exporting...' : 'Export CSV'}
          </ExportButton>
          <ExportButton 
            onClick={fetchAdvancedAnalytics} 
            disabled={advancedLoading}
            style={{ background: 'linear-gradient(45deg, #6f42c1, #8e44ad)' }}
          >
            <FaTable />
            {advancedLoading ? 'Loading...' : 'Advanced Analytics'}
          </ExportButton>
          <LogoutButton onClick={handleLogout}>
            Logout
          </LogoutButton>
        </HeaderActions>
      </Header>

      {/* Summary Stats */}
      <StatsGrid>
        <StatCard>
          <StatIcon><FaVideo /></StatIcon>
          <StatValue>{summary.unique_videos || 0}</StatValue>
          <StatLabel>Unique Videos</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon><FaUsers /></StatIcon>
          <StatValue>{summary.unique_subscribers || 0}</StatValue>
          <StatLabel>Unique Subscribers</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon><FaEye /></StatIcon>
          <StatValue>{video_analytics.reduce((sum, v) => sum + v.total_views, 0)}</StatValue>
          <StatLabel>Total Views</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon><FaClock /></StatIcon>
          <StatValue>{formatDuration(video_analytics.reduce((sum, v) => sum + v.total_watch_time_seconds, 0))}</StatValue>
          <StatLabel>Total Watch Time</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* Video Performance Chart */}
      <ChartSection>
        <ChartTitle>
          <FaVideo />
          Video Performance (Top 10)
        </ChartTitle>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.2)" />
              <XAxis 
                dataKey="video_id" 
                stroke="white" 
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis stroke="white" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: 'white'
                }}
                formatter={(value, name) => {
                  if (name === 'views') return [value, 'Views'];
                  if (name === 'completion') return [`${value}%`, 'Avg Completion'];
                  if (name === 'avg_time') return [formatDuration(value), 'Avg Watch Time'];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar dataKey="views" fill="#ffd700" name="Views" />
              <Bar dataKey="completion" fill="#ff6b6b" name="Avg Completion %" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px', opacity: 0.7 }}>
            No video data available
          </div>
        )}
      </ChartSection>

      {/* Subscriber Activity Table */}
      <TableSection>
        <ChartTitle>
          <FaUsers />
          Subscriber Activity
        </ChartTitle>
        {subscriber_analytics.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <TableHeader>Subscriber ID</TableHeader>
                <TableHeader>Videos Viewed</TableHeader>
                <TableHeader>Total Watch Time</TableHeader>
              </tr>
            </thead>
            <tbody>
              {subscriber_analytics.slice(0, 20).map((subscriber, index) => (
                <TableRow key={subscriber.subscriber_id || index}>
                  <TableCell>{subscriber.subscriber_id || 'Anonymous'}</TableCell>
                  <TableCell>{subscriber.videos_viewed}</TableCell>
                  <TableCell>{formatDuration(subscriber.total_watch_time_seconds)}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px', opacity: 0.7 }}>
            No subscriber data available
          </div>
        )}
      </TableSection>

      {summary.date_range && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '20px', 
          opacity: 0.7,
          fontSize: '0.9rem'
        }}>
          Data from {summary.date_range.start} to {summary.date_range.end}
        </div>
      )}

      {/* Advanced Analytics Modal */}
      {showAdvancedView && advancedData && (
        <ModalOverlay onClick={closeAdvancedView}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FaTable />
                Advanced Analytics View
              </ModalTitle>
              <CloseButton onClick={closeAdvancedView}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            {/* Summary Statistics */}
            <SummaryGrid>
              <SummaryCard>
                <SummaryValue>{advancedData.summary?.total_unique_combinations || 0}</SummaryValue>
                <SummaryLabel>Unique Subscriber+Video Combinations</SummaryLabel>
              </SummaryCard>
              <SummaryCard>
                <SummaryValue>{advancedData.summary?.unique_subscribers || 0}</SummaryValue>
                <SummaryLabel>Unique Subscribers</SummaryLabel>
              </SummaryCard>
              <SummaryCard>
                <SummaryValue>{advancedData.summary?.unique_videos || 0}</SummaryValue>
                <SummaryLabel>Unique Videos</SummaryLabel>
              </SummaryCard>
              <SummaryCard>
                <SummaryValue>{formatDuration(advancedData.summary?.total_watch_time_seconds || 0)}</SummaryValue>
                <SummaryLabel>Total Watch Time</SummaryLabel>
              </SummaryCard>
              <SummaryCard>
                <SummaryValue>{formatDuration(advancedData.summary?.average_watch_time || 0)}</SummaryValue>
                <SummaryLabel>Average Watch Time</SummaryLabel>
              </SummaryCard>
              <SummaryCard>
                <SummaryValue>{Math.round(advancedData.summary?.average_completion || 0)}%</SummaryValue>
                <SummaryLabel>Average Completion</SummaryLabel>
              </SummaryCard>
            </SummaryGrid>

            {/* Advanced Data Table */}
            <TableContainer>
              <AdvancedTable>
                <thead>
                  <tr>
                    <AdvancedTableHeader>Subscriber ID</AdvancedTableHeader>
                    <AdvancedTableHeader>Video ID</AdvancedTableHeader>
                    <AdvancedTableHeader>Timestamp</AdvancedTableHeader>
                    <AdvancedTableHeader>Watch Time</AdvancedTableHeader>
                    <AdvancedTableHeader>Completion %</AdvancedTableHeader>
                    <AdvancedTableHeader>Total Views</AdvancedTableHeader>
                  </tr>
                </thead>
                <tbody>
                  {advancedData.table_data?.map((row, index) => (
                    <AdvancedTableRow key={index}>
                      <AdvancedTableCell className="subscriber-id" title={row.subscriber_id}>
                        {row.subscriber_id || 'Anonymous'}
                      </AdvancedTableCell>
                      <AdvancedTableCell className="video-id" title={row.video_id}>
                        {row.video_id}
                      </AdvancedTableCell>
                      <AdvancedTableCell className="timestamp">
                        {new Date(row.timestamp).toLocaleString()}
                      </AdvancedTableCell>
                      <AdvancedTableCell>
                        {formatDuration(row.watch_time_seconds)}
                      </AdvancedTableCell>
                      <AdvancedTableCell>
                        {Math.round(row.watch_percentage)}%
                      </AdvancedTableCell>
                      <AdvancedTableCell>
                        {row.total_views}
                      </AdvancedTableCell>
                    </AdvancedTableRow>
                  )) || []}
                </tbody>
              </AdvancedTable>
            </TableContainer>

            {/* Debug Section */}
            {!showDebugSection ? (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <ExportButton 
                  onClick={fetchDebugData} 
                  disabled={debugLoading}
                  style={{ background: 'linear-gradient(45deg, #dc3545, #c82333)' }}
                >
                  🔍 {debugLoading ? 'Loading Debug Info...' : 'Show File Debug Info'}
                </ExportButton>
              </div>
            ) : (
              <div style={{ marginTop: '30px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '10px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <h3 style={{ margin: 0, color: '#ffd700' }}>🔍 File Discovery Debug Information</h3>
                  <button
                    onClick={() => setShowDebugSection(false)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Hide Debug
                  </button>
                </div>

                {debugData && (
                  <>
                    {/* Debug Summary */}
                    <SummaryGrid style={{ marginBottom: '20px' }}>
                      <SummaryCard>
                        <SummaryValue>{debugData.summary?.total_files || 0}</SummaryValue>
                        <SummaryLabel>Total Files Found</SummaryLabel>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryValue>{debugData.summary?.total_events || 0}</SummaryValue>
                        <SummaryLabel>Total Events</SummaryLabel>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryValue>{debugData.summary?.filtered_unique_subscribers || 0}</SummaryValue>
                        <SummaryLabel>Unique Subscribers (Filtered)</SummaryLabel>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryValue>{debugData.summary?.excluded_subscribers || 0}</SummaryValue>
                        <SummaryLabel>Excluded Subscribers</SummaryLabel>
                      </SummaryCard>
                    </SummaryGrid>

                    {/* Files Analysis Table */}
                    <TableContainer style={{ maxHeight: '300px' }}>
                      <AdvancedTable>
                        <thead>
                          <tr>
                            <AdvancedTableHeader>File Path</AdvancedTableHeader>
                            <AdvancedTableHeader>Source</AdvancedTableHeader>
                            <AdvancedTableHeader>Date</AdvancedTableHeader>
                            <AdvancedTableHeader>Events</AdvancedTableHeader>
                            <AdvancedTableHeader>Unique Subscribers</AdvancedTableHeader>
                            <AdvancedTableHeader>Filtered</AdvancedTableHeader>
                            <AdvancedTableHeader>Excluded</AdvancedTableHeader>
                          </tr>
                        </thead>
                        <tbody>
                          {debugData.files_analyzed?.map((file, index) => (
                            <AdvancedTableRow key={index}>
                              <AdvancedTableCell 
                                className="video-id" 
                                title={file.file_path}
                                style={{ maxWidth: '200px' }}
                              >
                                {file.file_path}
                              </AdvancedTableCell>
                              <AdvancedTableCell>
                                <span style={{ 
                                  padding: '2px 6px', 
                                  borderRadius: '3px', 
                                  fontSize: '0.7rem',
                                  background: file.source === 'azure' ? '#007bff' : '#28a745',
                                  color: 'white'
                                }}>
                                  {file.source}
                                </span>
                              </AdvancedTableCell>
                              <AdvancedTableCell className="timestamp">
                                {file.date}
                              </AdvancedTableCell>
                              <AdvancedTableCell>
                                {file.error ? (
                                  <span style={{ color: '#ff6b6b' }}>Error</span>
                                ) : (
                                  file.total_events
                                )}
                              </AdvancedTableCell>
                              <AdvancedTableCell>
                                {file.error ? '-' : file.unique_subscribers}
                              </AdvancedTableCell>
                              <AdvancedTableCell>
                                {file.error ? '-' : file.filtered_subscribers}
                              </AdvancedTableCell>
                              <AdvancedTableCell>
                                {file.error ? '-' : file.excluded_subscribers}
                              </AdvancedTableCell>
                            </AdvancedTableRow>
                          )) || []}
                        </tbody>
                      </AdvancedTable>
                    </TableContainer>

                    {/* All Unique Subscribers List */}
                    {debugData.all_unique_subscribers && debugData.all_unique_subscribers.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <h4 style={{ color: '#ffd700', marginBottom: '10px' }}>
                          All Unique Subscriber IDs Found ({debugData.all_unique_subscribers.length}):
                        </h4>
                        <div style={{ 
                          background: 'rgba(0, 0, 0, 0.3)', 
                          padding: '15px', 
                          borderRadius: '8px',
                          maxHeight: '150px',
                          overflowY: 'auto',
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          lineHeight: '1.4'
                        }}>
                          {debugData.all_unique_subscribers.map((id, index) => (
                            <div key={index} style={{ 
                              padding: '2px 0',
                              borderBottom: index < debugData.all_unique_subscribers.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                            }}>
                              {id}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Modal Actions */}
            <ModalActions>
              <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                Showing {advancedData.table_data?.length || 0} unique subscriber+video combinations
              </div>
              <ExportButton 
                onClick={handleAdvancedExportCSV} 
                disabled={advancedExportLoading}
                style={{ background: 'linear-gradient(45deg, #6f42c1, #8e44ad)' }}
              >
                <FaDownload />
                {advancedExportLoading ? 'Exporting...' : 'Download CSV'}
              </ExportButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default AnalyticsDashboard;
